import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Phone, Star, Globe, CheckCircle, ArrowRight,
  Shield, DollarSign, Plane, Award, Database, Building2,
  ExternalLink, ChevronLeft, Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProviderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authHeaders, user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [ptRecords, setPtRecords] = useState([]);
  const [ptLoading, setPtLoading] = useState(false);
  const [similarProviders, setSimilarProviders] = useState([]);

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const res = await axios.get(`${API}/providers/${id}`);
        setProvider(res.data);
        fetchRelated(res.data);
      } catch (e) {
        navigate('/providers');
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRelated = async (prov) => {
    // Fetch similar providers in same state
    try {
      const res = await axios.get(`${API}/providers`, { params: { state: prov.state, limit: 4 } });
      setSimilarProviders((res.data || []).filter(p => p.id !== prov.id).slice(0, 3));
    } catch (_) {}

    // Fetch hospital price transparency records for this provider
    if (prov.name) {
      setPtLoading(true);
      try {
        const res = await axios.post(`${API}/ai/search-intent`, {
          query: prov.name,
        });
        const raw = (res.data?.results || [])
          .filter(r => r.data_source === 'price_transparency')
          .slice(0, 20);
        setPtRecords(raw);
      } catch (_) {
      } finally {
        setPtLoading(false);
      }
    }
  };

  const handleContact = async () => {
    if (!user) { navigate('/auth'); return; }
    setContactLoading(true);
    try {
      await axios.post(`${API}/providers/${id}/contact`, {}, { headers: authHeaders });
      setContactSuccess(true);
    } catch (e) {
      console.error('Contact failed', e);
    } finally {
      setContactLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0EEE9] flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-[#A8B4B8]" />
      </div>
    );
  }

  if (!provider) return null;

  const sortedProcedures = [...(provider.procedures || [])].sort(
    (a, b) => (a.cash_price || Infinity) - (b.cash_price || Infinity)
  );

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-12">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 mb-6 transition-colors"
          >
            <ChevronLeft size={15} /> Back to results
          </button>

          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="flex-1">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-1 rounded">
                  {provider.type}
                </span>
                {provider.featured && (
                  <span className="inline-flex items-center gap-1.5 text-xs bg-[#0D3D4A] text-white font-semibold px-3 py-1 rounded" data-testid="verified-partner-callout">
                    <Award size={12} /> Verified Partner
                  </span>
                )}
                {provider.cash_pay_only && (
                  <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold px-2.5 py-1 rounded-full">
                    <DollarSign size={11} /> Cash Pay Only
                  </span>
                )}
                {provider.verified && (
                  <span className="inline-flex items-center gap-1 text-xs bg-[#0D3D4A]/10 text-[#0D3D4A] font-semibold px-2.5 py-1 rounded-full">
                    <CheckCircle size={11} /> Verified
                  </span>
                )}
                {provider.telehealth && (
                  <span className="text-xs bg-[#F0EEE9] text-[#0D3D4A] border border-[#0D3D4A]/20 px-2 py-0.5 rounded">
                    Telehealth Available
                  </span>
                )}
              </div>

              {provider.featured && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-[#F0EEE9] border border-[#0D3D4A]/20 rounded" data-testid="featured-banner">
                  <Award size={15} className="text-[#0D3D4A] flex-shrink-0" />
                  <p className="text-sm text-[#0D3D4A] font-medium">
                    Meridian Verified Partner — vetted for transparent pricing, accreditation, and patient satisfaction.
                  </p>
                </div>
              )}

              <h1
                className="text-2xl sm:text-3xl md:text-4xl font-semibold text-stone-900 mb-4"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                data-testid="provider-name"
              >
                {provider.name}
              </h1>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-stone-600">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#A8B4B8]" />
                  {provider.city}, {provider.state}
                </span>
                {provider.rating > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                    {provider.rating} ({provider.reviews?.toLocaleString()} reviews)
                  </span>
                )}
                {provider.accreditation && (
                  <span className="flex items-center gap-1.5">
                    <Shield size={14} className="text-[#0D3D4A]" />
                    {provider.accreditation} Accredited
                  </span>
                )}
                {provider.phone && (
                  <a href={`tel:${provider.phone}`} className="flex items-center gap-1.5 hover:text-[#0D3D4A] transition-colors">
                    <Phone size={14} className="text-[#A8B4B8]" />
                    {provider.phone}
                  </a>
                )}
                {provider.website && (
                  <a href={provider.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-[#0D3D4A] transition-colors">
                    <Globe size={14} className="text-[#A8B4B8]" />
                    <span className="truncate max-w-[180px]">{provider.website.replace(/^https?:\/\//, '')}</span>
                    <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── Main Content ─────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-8">

            {/* About */}
            {provider.about && (
              <div className="bg-white border border-stone-200 p-6 md:p-8 rounded-sm">
                <h2 className="text-xl font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  About This Center
                </h2>
                <p className="text-stone-600 leading-relaxed mb-5">{provider.about}</p>
                {provider.specialties?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {provider.specialties.map(s => (
                      <span key={s} className="text-sm bg-[#F0EEE9] text-[#0D3D4A] border border-[#0D3D4A]/20 px-3 py-1 rounded-full">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Procedures & Pricing */}
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <div className="px-6 md:px-8 pt-6 md:pt-8 pb-4">
                <h2 className="text-xl font-semibold text-stone-900 mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Cash-Pay Price Menu
                </h2>
                <p className="text-sm text-stone-500">
                  {sortedProcedures.length > 0
                    ? `${sortedProcedures.length} procedure${sortedProcedures.length === 1 ? '' : 's'} listed — all-inclusive bundled pricing`
                    : 'Contact provider for current pricing'}
                </p>
              </div>

              {sortedProcedures.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide border-y border-stone-100">
                        <th className="px-6 md:px-8 py-3 text-left font-medium">Procedure</th>
                        <th className="px-4 py-3 text-left font-medium whitespace-nowrap">CPT Code</th>
                        <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Cash Price</th>
                        <th className="px-4 py-3 text-left font-medium hidden md:table-cell">What's Included</th>
                        <th className="px-4 py-3 text-right font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {sortedProcedures.map((proc, i) => (
                        <tr
                          key={i}
                          className={`cursor-pointer transition-colors hover:bg-[#F0EEE9]/60 ${
                            selectedProcedure?.cpt === proc.cpt ? 'bg-[#F0EEE9]' : ''
                          }`}
                          onClick={() => setSelectedProcedure(proc)}
                          data-testid={`procedure-price-row-${i}`}
                        >
                          <td className="px-6 md:px-8 py-4 font-medium text-stone-900">{proc.name}</td>
                          <td className="px-4 py-4 text-stone-500 font-mono text-xs whitespace-nowrap">{proc.cpt || '—'}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-lg font-bold text-[#0D3D4A]">
                              {proc.cash_price ? `$${proc.cash_price.toLocaleString()}` : 'Call'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-stone-500 text-xs hidden md:table-cell">{proc.includes || '—'}</td>
                          <td className="px-4 py-4 text-right">
                            <button
                              onClick={e => { e.stopPropagation(); navigate(`/compare?provider=${provider.id}&cpt=${proc.cpt}`); }}
                              className="text-xs text-[#0D3D4A] hover:underline flex items-center gap-1 ml-auto whitespace-nowrap"
                              data-testid={`compare-btn-${i}`}
                            >
                              Compare <ArrowRight size={11} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 md:px-8 pb-8 text-stone-400 text-sm">
                  Contact provider for pricing information.
                </div>
              )}
              <p className="px-6 md:px-8 pb-5 text-xs text-[#A8B4B8]">
                * Prices are estimates. Confirm directly with the provider before scheduling.
              </p>
            </div>

            {/* Hospital Price Transparency Records */}
            {(ptLoading || ptRecords.length > 0) && (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <div className="px-6 md:px-8 py-5 border-b border-stone-100 flex items-center gap-2">
                  <Database size={16} className="text-[#A8B4B8]" />
                  <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Hospital Reported Prices
                  </h2>
                  <span className="ml-auto text-xs text-stone-400">CMS price transparency data</span>
                </div>

                {ptLoading ? (
                  <div className="flex items-center gap-2 px-6 py-8 text-stone-400 text-sm">
                    <Loader2 size={15} className="animate-spin" /> Loading price data…
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wide border-b border-stone-100">
                          <th className="px-6 md:px-8 py-3 text-left font-medium">Procedure</th>
                          <th className="px-4 py-3 text-left font-medium">Payer</th>
                          <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Reported Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {ptRecords.map((r, i) => (
                          <tr key={i} className="hover:bg-stone-50 transition-colors">
                            <td className="px-6 md:px-8 py-3 text-stone-800">
                              <span className="font-medium">{r.procedure_description || '—'}</span>
                              {r.cpt_code && <span className="ml-2 text-xs text-stone-400 font-mono">CPT {r.cpt_code}</span>}
                            </td>
                            <td className="px-4 py-3 text-stone-500 text-xs">{r.payer_name || 'Cash / Self-pay'}</td>
                            <td className="px-4 py-3 font-semibold text-[#0D3D4A] whitespace-nowrap">
                              {r.cash_price ? `$${r.cash_price.toLocaleString()}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Travel Estimator */}
            <div className="bg-[#F0EEE9] border border-stone-200 p-6 md:p-8 rounded-sm">
              <div className="flex items-center gap-3 mb-3">
                <Plane size={18} className="text-[#0D3D4A]" />
                <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Plan Your Trip
                </h2>
              </div>
              <p className="text-sm text-stone-600 mb-4">
                Even with travel costs, flying to {provider.city} could save you thousands. Use our travel estimator to see your all-in total.
              </p>
              <button
                onClick={() => navigate(`/travel?destination_state=${provider.state}&destination_city=${encodeURIComponent(provider.city)}`)}
                className="bg-[#0D3D4A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0E4557] transition-colors flex items-center gap-2 rounded-sm"
                data-testid="estimate-travel-btn"
              >
                Estimate Travel Costs <ArrowRight size={14} />
              </button>
            </div>

            {/* Similar Providers */}
            {similarProviders.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Similar Providers in {provider.state}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {similarProviders.map(p => {
                    const loPrice = p.procedures?.length
                      ? Math.min(...p.procedures.map(pr => pr.cash_price || Infinity))
                      : null;
                    const hiPrice = p.procedures?.length
                      ? Math.max(...p.procedures.map(pr => pr.cash_price || 0))
                      : null;
                    return (
                      <div
                        key={p.id}
                        className="bg-white border border-stone-200 p-5 cursor-pointer hover:border-[#0D3D4A]/40 transition-colors rounded-sm group"
                        onClick={() => navigate(`/provider/${p.id}`)}
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <Building2 size={14} className="text-[#A8B4B8] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-stone-900 text-sm leading-snug group-hover:text-[#0D3D4A] transition-colors">
                              {p.name}
                            </p>
                            <p className="text-xs text-stone-400 mt-0.5">{p.type}</p>
                          </div>
                        </div>
                        <p className="flex items-center gap-1 text-xs text-stone-500 mb-3">
                          <MapPin size={10} /> {p.city}, {p.state}
                        </p>
                        {loPrice && loPrice < Infinity && (
                          <p className="text-xs text-stone-600">
                            Prices from{' '}
                            <span className="font-semibold text-[#0D3D4A]">${loPrice.toLocaleString()}</span>
                            {hiPrice && hiPrice !== loPrice && ` – $${hiPrice.toLocaleString()}`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar ──────────────────────────────────────────── */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-white border border-stone-200 p-6 sticky top-24 rounded-sm">
              <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Contact This Provider
              </h3>

              {contactSuccess ? (
                <div className="text-center py-4">
                  <CheckCircle size={32} className="text-[#0D3D4A] mx-auto mb-3" />
                  <p className="font-semibold text-stone-900 mb-1 text-sm">Request Sent!</p>
                  <p className="text-xs text-stone-500 mb-4">
                    The provider has been notified. You can also contact them directly:
                  </p>
                  <a href={`tel:${provider.phone}`} className="text-[#0D3D4A] font-semibold text-sm block mb-2">{provider.phone}</a>
                  {provider.website && (
                    <a href={provider.website} target="_blank" rel="noreferrer" className="text-[#0D3D4A] text-xs underline">
                      Visit Website
                    </a>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-5 text-sm text-stone-600">
                    {provider.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-[#A8B4B8] flex-shrink-0" />
                        <a href={`tel:${provider.phone}`} className="hover:text-[#0D3D4A] transition-colors">{provider.phone}</a>
                      </div>
                    )}
                    {provider.website && (
                      <div className="flex items-center gap-2">
                        <Globe size={14} className="text-[#A8B4B8] flex-shrink-0" />
                        <a href={provider.website} target="_blank" rel="noreferrer" className="hover:text-[#0D3D4A] transition-colors truncate text-xs">
                          {provider.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <MapPin size={14} className="text-[#A8B4B8] flex-shrink-0 mt-0.5" />
                      <span className="text-xs">{provider.address}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleContact}
                    disabled={contactLoading}
                    className="w-full bg-[#0D3D4A] text-white py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60 mb-3 rounded-sm"
                    data-testid="contact-provider-btn"
                  >
                    {contactLoading ? 'Sending...' : 'Request Information'}
                  </button>
                  {!user && (
                    <p className="text-xs text-[#A8B4B8] text-center">Sign in to track your inquiry</p>
                  )}
                </>
              )}
            </div>

            {/* Selected Procedure Quick Action */}
            {selectedProcedure && (
              <div className="bg-[#F0EEE9] border border-[#0D3D4A]/20 p-5 rounded-sm">
                <h4 className="font-semibold text-[#0A2E37] mb-2 text-sm">Selected Procedure</h4>
                <p className="font-medium text-stone-900 mb-1 text-sm">{selectedProcedure.name}</p>
                {selectedProcedure.cash_price && (
                  <p className="text-2xl font-bold text-[#0D3D4A] mb-4">
                    ${selectedProcedure.cash_price.toLocaleString()}
                  </p>
                )}
                <button
                  onClick={() => navigate(`/compare?provider=${provider.id}&cpt=${selectedProcedure.cpt}`)}
                  className="w-full bg-[#0D3D4A] text-white py-2.5 text-sm font-medium hover:bg-[#0E4557] transition-colors flex items-center justify-center gap-2 rounded-sm"
                >
                  Compare vs. Insurance <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
